import { OpenBankingAccount } from '@features/openBanking/types/openBankingAccount'
import { Account } from '@modules-deprecated/app/accounts/types'

import { BankAccountSource } from '../enums/bankAccountSource'
import { BankAccount } from '../types/bankAccount'

interface SortBankAccountsParams {
  accounts: Account[]
  bankAccounts?: BankAccount[]
  openBankingAccounts?: OpenBankingAccount[]
}

export const sortBankAccounts = ({
  accounts,
  bankAccounts,
  openBankingAccounts,
}: SortBankAccountsParams): Account[] => {
  const bankAccountByAccountId = new Map(bankAccounts?.map((ba) => [ba.bookkeepingAccountId, ba]))
  const bankConnectionByAccountId = new Map(openBankingAccounts?.map((bc) => [bc.externalAccountId, bc]))

  const directlyConnectedAccounts = accounts.filter((account) => {
    const bankAccount = bankAccountByAccountId.get(account.id)
    return !!bankAccount && bankAccount.source === BankAccountSource.SHINE_BANKING
  })

  const otherConnectedAccounts = accounts.filter((account) => bankConnectionByAccountId.has(account.id))

  const connectedAccountIds = [...directlyConnectedAccounts, ...otherConnectedAccounts].map((account) => account.id)
  const unconnectedAccounts = accounts.filter((account) => !connectedAccountIds.includes(account.id))

  // Directly connected: alphabetically by account name, then by IBAN ascending
  const sortedDirectlyConnectedAccounts = directlyConnectedAccounts.sort((a, b) => {
    const nameCompare = (a.name ?? '').localeCompare(b.name ?? '')
    if (nameCompare !== 0) {
      return nameCompare
    }

    const ibanA = bankAccountByAccountId.get(a.id)?.iban ?? ''
    const ibanB = bankAccountByAccountId.get(b.id)?.iban ?? ''
    return ibanA.localeCompare(ibanB)
  })

  // Open banking: alphabetically by bank name, then by IBAN ascending
  const sortedOpenBankingAccounts = otherConnectedAccounts.sort((a, b) => {
    const connectionA = bankConnectionByAccountId.get(a.id)
    const connectionB = bankConnectionByAccountId.get(b.id)

    const bankNameCompare = (connectionA?.bank?.name ?? '').localeCompare(connectionB?.bank?.name ?? '')
    if (bankNameCompare !== 0) {
      return bankNameCompare
    }

    return (connectionA?.iban ?? '').localeCompare(connectionB?.iban ?? '')
  })

  // Unconnected: alphabetically by account name
  const sortedUnconnectedAccounts = unconnectedAccounts.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))

  return [...sortedDirectlyConnectedAccounts, ...sortedOpenBankingAccounts, ...sortedUnconnectedAccounts]
}
